/**
 * SearchBox Component - Search input form with URL synchronization - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Provides a search input form that synchronizes with URL query parameters
 * and navigates to search results page. Features query persistence and
 * form validation with automatic URL encoding.
 * 
 * @component
 * @returns {JSX.Element} The SearchBox component with search form
 * 
 * @example 
 * <SearchBox />
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

function SearchBox() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryp = queryParams.get("query") || "";
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  // Synchronize input with URL query parameter
  useEffect(() => {
    /**
     * Updates the local query state when URL query parameter changes
     * Ensures the search box reflects the current URL state
     */
    setQuery(queryp);
  }, [queryp]);

  /**
   * Handles search form submission
   * Validates query input and navigates to search results page with encoded query
   * 
   * @param {Event} e - Form submission event
   */
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (query.trim() !== "") {
      navigate(`/search?query=${encodeURIComponent(query)}`);
    }
  };

  /**
   * Handles search input value changes
   * Updates the local query state as user types
   * 
   * @param {Event} e - Input change event
   */
  const handleSearchChange = (e) => {
    setQuery(e.target.value);
  };

  return (
    <div>
      <form className="d-flex search-form search-box" onSubmit={handleSearchSubmit}>
        <div className="search-input-container">
          <input
            type="search"
            className="form-control search-input"
            placeholder="Search"
            aria-label="Search"
            value={query}
            onChange={handleSearchChange}
          />
          <i className="fas fa-magnifying-glass search-icon"></i>
        </div>
      </form>
    </div>
  );
}

export default SearchBox;
